import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Button, CircularProgress, Grid, IconButton, LinearProgress, Typography } from "@mui/material";
import axios from "axios";
import Field from "components/Field";
import { email as emailValidate } from "components/Field/validate";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getClients } from "store/actions/clients";
import { getProfile } from "store/actions/user";

const ONBOARDING_VIDEO_SRC = "https://evocoach.nyc3.cdn.digitaloceanspaces.com/assets/evocoach-intro.mp4";

const STEPS = [
  { label: "Présentation" },
  { label: "Premier client" },
];

const OnboardingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userProfile = useSelector((state) => state.user.userProfile);
  const [step, setStep] = useState(1);
  const [addedClient, setAddedClient] = useState(null);

  const firstName = location.state?.firstName || userProfile?.first_name || "";

  useEffect(() => {
    if (!userProfile || Object.keys(userProfile).length === 0) {
      dispatch(getProfile());
    }
  }, []);

  const handleAddClient = (values, { setSubmitting }) => {
    axios
      .post("/api/coach/invite/client", values)
      .then(() => {
        dispatch(getClients());
        setAddedClient(values);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Une erreur est survenue.");
        setSubmitting(false);
      });
  };


  return (
    <Box
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      bgcolor="#F5F5F5"
      px={{ xs: 2, sm: 4 }}
      py={{ xs: 4, md: 6 }}
    >
      {/* Logo */}
      <Box mb={{ xs: 3, md: 4 }}>
        <img
          src="/assets/images/evocoach-logo.png"
          alt="Evocoach"
          style={{ height: 36 }}
        />
      </Box>

      {/* Step progress */}
      <Box mb={4} width="100%" maxWidth={800}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            {step > 1 && (
              <IconButton
                size="small"
                onClick={() => setStep((s) => s - 1)}
                sx={{ mr: 0.5, color: "text.secondary" }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Typography variant="caption" color="text.secondary">
              Étape {step} sur {STEPS.length}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {STEPS[step - 1].label}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={(step / STEPS.length) * 100}
          sx={{ borderRadius: 4, height: 6 }}
        />
      </Box>

      {/* Step 1 — Video */}
      {step === 1 && (
        <>
          <Typography
            variant="h4"
            fontWeight={700}
            textAlign="center"
            mb={1}
            sx={{ fontSize: { xs: "1.6rem", md: "2.2rem" } }}
          >
            {firstName ? `Bienvenue, ${firstName}\u00a0!` : "Bienvenue\u00a0!"}
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            mb={{ xs: 3, md: 4 }}
            sx={{ maxWidth: 520 }}
          >
            Découvrez en quelques minutes comment Evocoach peut transformer
            votre activité de coaching.
          </Typography>

          <Box
            sx={{
              width: "100%",
              maxWidth: 800,
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0px 8px 40px rgba(0, 0, 0, 0.15)",
              aspectRatio: "16 / 9",
            }}
          >
            <Box
              component="video"
              src={ONBOARDING_VIDEO_SRC}
              controls
              sx={{ display: "block", width: "100%", height: "100%" }}
            />
          </Box>

          <Box
            mt={{ xs: 3, md: 4 }}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1.5}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => setStep(2)}
              sx={{
                px: 5,
                py: 1.5,
                borderRadius: 8,
                fontWeight: 600,
                fontSize: "1rem",
                minWidth: 240,
              }}
            >
              Continuer →
            </Button>
            <Button
              variant="text"
              onClick={() => navigate("/tutoriels")}
              sx={{ color: "text.secondary", fontSize: "0.85rem" }}
            >
              Voir plus de tutoriels
            </Button>
          </Box>
        </>
      )}

      {/* Step 2 — Add first client */}
      {step === 2 && !addedClient && (
        <>
          <Typography
            variant="h4"
            fontWeight={700}
            textAlign="center"
            mb={1}
            sx={{ fontSize: { xs: "1.6rem", md: "2.2rem" } }}
          >
            Ajoutez votre premier client
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            mb={0.5}
            sx={{ maxWidth: 480 }}
          >
            Invitez un client pour commencer. Il recevra un e-mail pour accéder
            à son espace personnel.
          </Typography>

          <Typography
            variant="body2"
            color="text.disabled"
            textAlign="center"
            mb={{ xs: 3, md: 4 }}
            sx={{ maxWidth: 480 }}
          >
            Vous pouvez aussi vous ajouter vous-même avec une autre adresse
            e-mail pour tester la plateforme.
          </Typography>

          <Formik
            initialValues={{ first_name: "", last_name: "", email: "" }}
            onSubmit={handleAddClient}
          >
            {({ handleSubmit, isSubmitting }) => (
              <Box
                component="form"
                onSubmit={handleSubmit}
                width="100%"
                maxWidth={480}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      type="text"
                      label="Prénom"
                      placeholder="Prénom"
                      name="first_name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      type="text"
                      label="Nom"
                      placeholder="Nom"
                      name="last_name"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      type="text"
                      label="Adresse e-mail"
                      placeholder="email@exemple.com"
                      name="email"
                      validate={emailValidate}
                    />
                  </Grid>
                </Grid>

                <Box mt={3} display="flex" flexDirection="column" gap={1.5}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    fullWidth
                    sx={{ py: 1.5, borderRadius: 8, fontWeight: 600 }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Ajouter ce client →"
                    )}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate("/")}
                    sx={{ color: "text.secondary", fontSize: "0.85rem" }}
                  >
                    Passer cette étape
                  </Button>
                </Box>
              </Box>
            )}
          </Formik>
        </>
      )}

      {/* Step 2 — Success confirmation */}
      {step === 2 && addedClient && (
        <>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 64, color: "success.main", mb: 2 }}
          />

          <Typography
            variant="h4"
            fontWeight={700}
            textAlign="center"
            mb={1}
            sx={{ fontSize: { xs: "1.6rem", md: "2.2rem" } }}
          >
            Client ajouté !
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            mb={0.5}
            sx={{ maxWidth: 440 }}
          >
            {addedClient.first_name
              ? `${addedClient.first_name}${addedClient.last_name ? " " + addedClient.last_name : ""} a bien été invité.`
              : "Votre client a bien été invité."}{" "}
            Un e-mail lui a été envoyé pour accéder à son espace.
          </Typography>

          <Typography
            variant="body2"
            color="text.disabled"
            textAlign="center"
            mb={{ xs: 3, md: 4 }}
            sx={{ maxWidth: 440 }}
          >
            Vous pourrez lui envoyer un programme fitness ou nutrition depuis
            son profil dans le tableau de bord.
          </Typography>

          <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} width="100%" maxWidth={480}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/")}
              fullWidth
              sx={{ py: 1.5, borderRadius: 8, fontWeight: 600 }}
            >
              Accéder au tableau de bord →
            </Button>
            <Button
              variant="text"
              onClick={() => setAddedClient(null)}
              sx={{ color: "text.secondary", fontSize: "0.85rem" }}
            >
              Ajouter un autre client
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default OnboardingPage;
